import React, { useState, useEffect, useCallback } from 'react';
import Navigation from './Navigation';
import apiService from '../services/api';
import { decodeFuelType, formatPrice } from '../constants/carFilters';
import { applyTemplate } from '../utils/smsFormat';
import Spinner from './Spinner';
import Toast from './Toast';

// Kode poškodb (iz scraperja) -> lepe oznake + barve.
const DAMAGE_LABELS = {
  KARAMBOLIRANO: { text: 'Karambolirano', bg: '#dc3545' },
  POPLAVLJENO: { text: 'Poplavljeno', bg: '#0d6efd' },
  OKVARA: { text: 'V okvari', bg: '#fd7e14' },
  NI_VOZNO: { text: 'Ni vozno', bg: '#6c757d' },
  POSKODOVANO: { text: 'Poškodovano', bg: '#b02a37' },
};

const MAX_MESSAGE = 100;

/**
 * "V okvari" — poškodovana vozila fizičnih oseb (karambolirano / v okvari / poplavljeno).
 * Prodajalcu lahko pošlješ SMS (odkup). Vir: GET /cars/damaged.
 */
export default function DamagedBoard() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState({});
  const [sendStatus, setSendStatus] = useState({});
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [carsRes, profileRes] = await Promise.all([
        apiService.getDamagedCars(168),
        apiService.getSmsProfile().catch(() => null),
      ]);
      setCars((carsRes && carsRes.data) || []);
      if (profileRes && profileRes.data) setProfile(profileRes.data);
    } catch (e) {
      /* non-critical */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const badges = (damageStatus) =>
    String(damageStatus || '').split(',').map((c) => c.trim()).filter(Boolean).map((code) => {
      const d = DAMAGE_LABELS[code] || { text: code, bg: '#6c757d' };
      return (
        <span key={code} style={{ display: 'inline-block', background: d.bg, color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600, marginRight: 6, marginTop: 4 }}>
          {d.text}
        </span>
      );
    });

  const handleSend = async (car) => {
    const raw = (messages[car.id] !== undefined ? messages[car.id] : (profile && profile.defaultSmsMessage) || '').trim();
    if (!raw) {
      setSendStatus((s) => ({ ...s, [car.id]: { type: 'error', text: 'Sporočilo je prazno (nastavi privzeto v SMS plošči).' } }));
      return;
    }
    setSendStatus((s) => ({ ...s, [car.id]: { type: 'sending', text: 'Pošiljam …' } }));
    try {
      const res = await apiService.sendSms({
        avtonetId: car.avtonetId,
        recipientPhone: car.phone,
        message: applyTemplate(raw, car),
        filterUsed: 'V okvari (odkup)',
      });
      const ok = res.data && res.data.sent;
      setSendStatus((s) => ({ ...s, [car.id]: { type: ok ? 'ok' : 'warn', text: ok ? '✓ Poslano' : ('⚠ ' + ((res.data && (res.data.error || res.data.note)) || 'ni poslano')) } }));
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.error) || 'Napaka';
      setSendStatus((s) => ({ ...s, [car.id]: { type: 'error', text: '✗ ' + msg } }));
      setToast({ type: 'error', message: msg });
    }
  };

  return (
    <div>
      <Navigation />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 20 }}>
        <h2>V okvari</h2>
        <p style={{ color: '#666', marginTop: -6 }}>
          Poškodovana vozila <strong>fizičnih oseb</strong> (karambolirano / v okvari / poplavljeno). Prodajalcu lahko pošlješ SMS — npr. ponudbo za odkup.
        </p>
        {loading ? (
          <Spinner />
        ) : cars.length === 0 ? (
          <p style={{ color: '#888' }}>Trenutno ni poškodovanih vozil fizičnih oseb.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {cars.map((car) => {
              const msg = messages[car.id] !== undefined ? messages[car.id] : ((profile && profile.defaultSmsMessage) || '');
              const status = sendStatus[car.id];
              const title = `${car.znamka || ''} ${car.model || ''}`.trim() || 'Vozilo';
              return (
                <div key={car.id} className="damaged-row" style={{ border: '1px solid #e0e0e0', borderRadius: 10, padding: 14, background: '#fff', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 260px' }}>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>
                      <a href={car.avtonetId ? `https://www.avto.net/Ads/details.asp?id=${car.avtonetId}` : undefined} target="_blank" rel="noreferrer" style={{ color: '#222', textDecoration: 'none' }}>{title}</a>
                    </div>
                    <div style={{ color: '#0066cc', fontWeight: 700, fontSize: 16 }}>{formatPrice(car.cena)}</div>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                      {[car.letnik && `📅 ${car.letnik}`, car.mileage && `🛣️ ${car.mileage}`, car.gorivo && `⛽ ${decodeFuelType(car.gorivo)}`, car.viewLocation && `📍 ${car.viewLocation}`].filter(Boolean).join('   ·   ')}
                    </div>
                    <div style={{ marginTop: 4 }}>{badges(car.damageStatus)}</div>
                    <div style={{ marginTop: 6, fontWeight: 600, color: '#007AFF' }}>📞 {car.phone || '—'}</div>
                  </div>
                  <div style={{ flex: '1 1 300px' }}>
                    <textarea value={msg} maxLength={MAX_MESSAGE} rows={2} onChange={(e) => setMessages((m) => ({ ...m, [car.id]: e.target.value }))} placeholder="Sporočilo za prodajalca (do 100 znakov)" style={{ width: '100%', padding: 7, boxSizing: 'border-box' }} />
                    <div style={{ fontSize: 12, color: '#888' }}>{msg.length}/{MAX_MESSAGE}</div>
                    {status ? <div style={{ fontSize: 12, marginTop: 2, color: status.type === 'error' ? '#d00' : status.type === 'ok' ? '#080' : '#a60' }}>{status.text}</div> : null}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button onClick={() => handleSend(car)} disabled={!car.phone || (status && status.type === 'sending')} style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>💬 Pošlji SMS</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Toast message={toast && toast.message} type={toast && toast.type} onClose={() => setToast(null)} />
    </div>
  );
}
